import React, { useCallback } from "react";
import slugify from "slugify";
import { Field, useField } from "react-final-form";
import ReactTooltip from "react-tooltip";
import Select from "react-select";
import classnames from "classnames";
import { history, Error } from "./Shared";
import Autocomplete from "./Autocomplete";
import { getDonationPresets } from "../../helpers/DonationPresets";

const autocompleteRef = React.createRef();
const frequencyRef = React.createRef();

const frequencyOptions = [
  { value: "Fortnightly", label: "Fortnightly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Half-Yearly", label: "Half Yearly" },
];

const dropdownStyles = {
  container: (baseStyles, state) => ({
    ...baseStyles,
    height: 40,
  }),
  control: (baseStyles, state) => ({
    ...baseStyles,
    border: "1px solid #acb5bc",
    borderRadius: 0,
    height: 40,
  }),
  valueContainer: (baseStyles, state) => ({
    ...baseStyles,
    height: 40,
    alignItems: "center",
    paddingTop: 0,
    paddingBottom: 0,
    fontWeight: 500,
    fontSize: 14,
    textAlign: "center",
    color: "#5a5a62",
  }),
  placeholder: (baseStyles, state) => ({
    ...baseStyles,
    overflow: "visible",
  }),
  input: (baseStyles, state) => ({
    ...baseStyles,
    height: 40,
    marginTop: 0,
    marginBottom: 0,
  }),
  singleValue: (baseStyles, state) => ({
    ...baseStyles,
    overflow: "visible",
  }),
};

const Donation = ({ support }) => {
  const missionId = useField("mission_id");
  const afesGeneralFund = useField("afes_general_fund");
  const missionName = useField("mission_name");
  const frequency = useField("frequency");
  const otherPayment = useField("other_payment_schedule");
  const paymentSchedule = useField("payment_schedule");
  const amount = useField("amount");
  const paymentAmt = useField("payment_amt");
  const PaymentInput = useField("donation-amount-other");
  const donationPresets = getDonationPresets(frequency.input.value);

  const resetAmount = useCallback(() => {
    amount.input.onChange("");
    paymentAmt.input.onChange(undefined);
    PaymentInput.input.onChange(null);
  }, [amount, paymentAmt, PaymentInput]);

  return (
    <div id="donationForm" className="form donation_panel__content">
      <div className="form__block">
        <div className="donation_panel__header">
          <span className="tabbed_nav__number">1</span>
          <span className="tabbed_nav__title">Donation</span>
        </div>
        <h3 className="donation_panel__title">
          Who would you like to support?
        </h3>
        <div className="row">
          <div className="col-8 donation_panel__autocomplete">
            <Field name="donorlist" placeholder="Search for a Person or Campus">
              {(props) => (
                <Autocomplete
                  ref={autocompleteRef}
                  suggestions={support}
                  onSelected={(obj) => {
                    if (obj?.id) {
                      missionId.input.onChange(obj.id);
                      missionName.input.onChange(obj.name);
                      afesGeneralFund.input.onChange(null);
                      props.input.onChange(obj.name);
                      const slug = `/support-${slugify(obj.name, {
                        lower: true,
                      })}-${obj.id}`;
                      history.push(slug);
                    } else {
                      missionId.input.onChange(null);
                    }
                  }}
                  {...props.input}
                />
              )}
            </Field>
            <div
              className="search_info_icon hide_at_bp_small"
              data-tip
              data-for="hover"
            />
            <ReactTooltip
              className="donation_panel__tooltip"
              effect="solid"
              place="top"
              type="info"
              id="hover"
            >
              Can't find who you would like to support?{" "}
              <span>Contact 1800 989 028</span>
            </ReactTooltip>
          </div>
          <div className="col-1 donation_panel__span">or</div>
          <div className="col-3 form__radioGroup">
            <div className="form__radio">
              <Field
                type="radio"
                name="afes_general_fund"
                value="4249"
                render={(props) => (
                  <input
                    id="afes_general_fund"
                    type="radio"
                    {...props.input}
                    onClick={(e) => {
                      missionId.input.onChange("4249");
                      missionName.input.onChange("Where most needed");
                    }}
                    onChange={(e) => {
                      autocompleteRef.current.clear();
                      missionId.input.onChange("4249");
                      missionName.input.onChange("Where most needed");
                      props.input.onChange(e);
                    }}
                  />
                )}
              />
              <label
                htmlFor="afes_general_fund"
                className={classnames("radio_label", {
                  active: afesGeneralFund.input.value,
                })}
              >
                Where most needed{" "}
              </label>
            </div>
          </div>
        </div>
        <Error name="mission_id" />
      </div>
      <div className="form__block">
        <h3 className="donation_panel__title">
          How often would you like to give
          {missionName.input.value ? " to " : ""}
          <span>{missionName.input.value}</span>?
        </h3>
        <div className="row">
          <div className="col-8 form__radioGroup">
            <div className="form__radio col-4">
              <label
                className={classnames("radio_label", {
                  active: frequency.input.value === "One-time Gift",
                })}
              >
                <Field
                  name="payment_schedule"
                  type="radio"
                  className="radio_input"
                  value="One-time Gift"
                  id="one-time-gift"
                  render={(props) => (
                    <input
                      name="payment_schedule"
                      type="radio"
                      {...props.input}
                      onChange={(e) => {
                        props.input.onChange(e);
                        otherPayment.input.onChange("");
                        frequency.input.onChange("One-time Gift");
                        resetAmount();
                      }}
                    />
                  )}
                />{" "}
                One-time gift
              </label>
            </div>
            <div className="form__radio col-4">
              <label
                className={classnames("radio_label", {
                  active: frequency.input.value === "Monthly",
                })}
              >
                <Field
                  name="payment_schedule"
                  type="radio"
                  value="Monthly"
                  id="frequency-montly"
                  render={(props) => (
                    <input
                      name="frequency-montly"
                      type="radio"
                      {...props.input}
                      onChange={(e) => {
                        props.input.onChange(e);
                        otherPayment.input.onChange("");
                        frequency.input.onChange("Monthly");
                        resetAmount();
                      }}
                    />
                  )}
                />
                Monthly
              </label>
            </div>
            <div className="form__radio col-4">
              <label
                className={classnames("radio_label", {
                  active: frequency.input.value === "Annually",
                })}
              >
                <Field
                  name="payment_schedule"
                  type="radio"
                  value="Annually"
                  id="frequency-anually"
                  render={(props) => (
                    <input
                      name="frequency-anually"
                      type="radio"
                      {...props.input}
                      onChange={(e) => {
                        props.input.onChange(e);
                        otherPayment.input.onChange("");
                        frequency.input.onChange("Annually");
                        resetAmount();
                      }}
                    />
                  )}
                />
                Annually
              </label>
            </div>
          </div>

          <div className="col-1 donation_panel__span">or</div>
          <div className="col-3">
            <Field name="other_payment_schedule">
              {(props) => {
                return (
                  <Select
                    styles={dropdownStyles}
                    ref={frequencyRef}
                    placeholder="Other"
                    className={classnames("rounded", {
                      active: otherPayment.input.value,
                    })}
                    field="other_payment_schedule"
                    id="other_payment_schedule"
                    {...props.input}
                    value={
                      frequencyOptions.find(
                        (option) => option.value === props.input.value,
                      ) || ""
                    }
                    onChange={(e) => {
                      if (e) {
                        props.input.onChange(e.value);
                        if (e.value) {
                          frequency.input.onChange(e.value);
                          paymentSchedule.input.onChange(undefined);
                          resetAmount();
                        }
                      } else {
                        props.input.onChange(e);
                      }
                    }}
                    options={frequencyOptions}
                  />
                );
              }}
            </Field>
          </div>
        </div>
        <Error name="frequency" />
      </div>
      <div className="form__block">
        <h3 className="donation_panel__title">
          How much would you like to give
          {missionName.input.value ? " to " : ""}
          <span>{missionName.input.value}</span>?
        </h3>
        <div className="row">
          <div className="col-8 form__radioGroup">
            {donationPresets.map((preset) => {
              const inputId = `donation-${preset}`;

              return (
                <div className="form__radio col-4" key={preset}>
                  <label
                    htmlFor={inputId}
                    className={classnames("radio_label", {
                      active: amount.input.value === preset,
                    })}
                  >
                    <Field
                      value={preset}
                      type="radio"
                      name="payment_amt"
                      render={(props) => (
                        <input
                          name="payment_amt"
                          type="radio"
                          className="radio_input"
                          {...props.input}
                          onChange={(e) => {
                            props.input.onChange(e);
                            PaymentInput.input.onChange(null);
                            amount.input.onChange(e.target.value);
                          }}
                          id={inputId}
                        />
                      )}
                    />
                    ${preset}
                  </label>
                </div>
              );
            })}
          </div>

          <div className="col-1 donation_panel__span">or</div>
          <div className="col-3">
            <div className="form_input_wrap">
              <div className="form_input_icon form_input_icon--dollar" />
              <Field name="donation-amount-other">
                {(props) => (
                  <input
                    label=""
                    type="text"
                    id="donation-amount-other"
                    name="donation-amount-other"
                    placeholder="Enter amount"
                    autoComplete=""
                    className="flex-row__stretch form_input_with_dollar"
                    {...props.input}
                    onKeyDown={(e) => {
                      // Suppress the key if it is not a number, backspace, tab, left arrow, or right arrow.
                      // NB Spaces are numbers … apparently … so we suppress keycode 32, which covers space, option-space, etc.
                      if (
                        (isNaN(e.key) || e.keyCode === 32) &&
                        e.keyCode !== 8 &&
                        e.keyCode !== 9 &&
                        e.keyCode !== 37 &&
                        e.keyCode !== 39
                      ) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                      if (e.shiftKey) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    onChange={(e) => {
                      paymentAmt.input.onChange(undefined);
                      props.input.onChange(e);
                      amount.input.onChange(e.target.value);
                    }}
                  />
                )}
              </Field>
            </div>
          </div>
        </div>
        <Error name="amount" />
      </div>
    </div>
  );
};

export default Donation;
